import { Component, Input, booleanAttribute } from '@angular/core';

/**
 * Floating notification badge that overlays a count on its parent element.
 * Wrap any icon or avatar with this component to show a counter badge.
 *
 * Usage:
 *   <app-floating-badge [count]="unreadCount()" [show]="hasUnread()">
 *     <img src="assets/icons/bell.svg" alt="Notifications">
 *   </app-floating-badge>
 *
 * Variants:
 *   - 'dot'   : small circle, no text (e.g., tab bar)
 *   - 'count' : pill with number (default)
 *
 * Positions: 'top-right' (default) | 'top-left' | 'inline'
 */
@Component({
  selector: 'app-floating-badge',
  standalone: true,
  templateUrl: './floating-badge.html',
  styleUrl: './floating-badge.scss'
})
export class FloatingBadgeComponent {
  /** Numeric count to display */
  @Input() count: number = 0;

  /** Whether to show the badge (convenience — also hidden when count is 0 for 'count' variant) */
  @Input({ transform: booleanAttribute }) show: boolean = false;

  /** Visual variant */
  @Input() variant: 'dot' | 'count' = 'count';

  /** Badge position relative to the slotted content */
  @Input() position: 'top-right' | 'top-left' | 'inline' = 'top-right';

  /** Max value before showing "N+" */
  @Input() max: number = 99;

  get displayValue(): string {
    if (this.variant === 'dot') return '';
    return this.count > this.max ? `${this.max}+` : `${this.count}`;
  }

  get isVisible(): boolean {
    return this.show && (this.variant === 'dot' || this.count > 0);
  }
}
